<template>
  <div class="messages-conversations-filter pt-2 pl-2 pr-2">
    <div class="input-icon mb-3">
      <input 
        type="text" 
        class="form-control"
        v-model="conversationsQuery"
        @input="handleSearch"
        :placeholder="t('messages.search_placeholder')"
      >
      <span class="input-icon-addon">
        <i class="fe fe-search"></i>
      </span>
    </div>
  </div>
  <div class="messages-conversations" v-if="hasContacts">
    <div class="wrapper-conversations" data-lazy-img-container>
      <div class="conversations list-group list-group-flush">
        <a 
          href="#" 
          v-for="item in conversations" 
          :key="item.contact?.id || item.id"
          @click="selectContact(item.contact.id)"
          class="conversation list-group-item list-group-item-action flex-column align-items-start d-block p-3"
          :class="{
            premium: item.contact?.premium !== false,
            active: item.contact?.id === currentContactId,
          }"
        >
          <div class="d-flex w-100 justify-content-start align-content-center">
            <div class="pr-3 align-self-center">
              <div class="avatar" :style="{ backgroundImage: 'url(' + item.contact?.avatar + ')' }">
                <span class="avatar-status" :class="onlineClass(item.contact?.id, item.contact?.online)" v-show="hasOnlineIndicator(item.contact?.id, item.contact?.online)"></span>
              </div>
            </div>
            <div class="align-self-center flex-grow-1">
              <div class="d-flex justify-content-between align-items-center mb-1">
                <h5 class="name mb-0">
                  {{ item.contact?.full_name }}<span v-if="item.contact?.age">, {{ item.contact.age }}</span>
                </h5>
                <small class="text-muted" v-if="item.last_message?.timestamp">
                  {{ relativeTime(item.last_message.timestamp) }}
                </small>
              </div>
              <div class="d-flex justify-content-between align-items-center">
                <div class="message" :class="{ 'text-muted font-italic': isTyping(item.contact?.id) }">
                  <span v-if="isTyping(item.contact?.id)" class="typing-header-indicator">
                    <span class="typing-header-indicator-text">{{ t('messages.typing') }}</span>
                    <span class="typing-header-dots" aria-hidden="true">
                      <span></span><span></span><span></span>
                    </span>
                  </span>
                  <span v-else-if="getDraftText(item.contact?.id)" class="draft-text"><span class="text-danger draft-label">{{ t('messages.draft') }}: </span>{{ processSidebarMessageText(getDraftText(item.contact?.id)) }}</span>
                  <span v-else-if="item.last_message?.hasMedia" class="d-flex align-items-center"><i class="fe fe-image mr-1"></i>{{ t('messages.photo') }}</span>
                  <span v-else-if="item.last_message?.text">{{ processSidebarMessageText(item.last_message.text) }}</span>
                </div>
                <div class="d-flex align-items-center">
                  <span
                    class="badge badge-danger badge-pill nav-badge sidebar-unread-badge"
                    v-if="getUnreadCount(item.contact?.id)"
                    :data-contact-id="item.contact?.id"
                  >
                    {{ getUnreadCount(item.contact?.id) }}
                  </span>
                  
                                    
                  <small v-if="item.last_message?.type === 'sent' && !getUnreadCount(item.contact?.id)" class="message-status-time"
                         :class="(item.last_message?.is_read === true || item.last_message?.is_read === 1 || item.last_message?.status === 'read') ? 'text-primary' : 'text-gray'">
                    <i v-if="item.last_message?.status === 'pending'" class="fa fa-spinner fa-spin" :title="t('messages.sending')"></i>
                    <span v-else-if="item.last_message?.is_read === true || item.last_message?.is_read === 1 || item.last_message?.status === 'read'" 
                          class="message-status-read" 
                          :title="t('messages.read_status')">
                      <i class="fa fa-check"></i>
                      <i class="fa fa-check"></i>
                    </span>
                    <span v-else-if="item.last_message?.status === 'delivered'" 
                          class="message-status-delivered" 
                          :title="t('messages.delivered_status')">
                      <i class="fa fa-check"></i>
                      <i class="fa fa-check"></i>
                    </span>
                    <i v-else-if="item.last_message?.status === 'sent'" 
                       class="fa fa-check" 
                       :title="t('messages.sent_status')"></i>
                    <i v-else class="fa fa-check" :title="t('messages.sent_status')"></i>
                  </small>
                </div>
              </div>
            </div>
          </div>
        </a>
      </div>
    </div>
  </div>
  <div class="no-conversations-found" v-if="conversationsQuery.length > 0 && !hasContacts">
    <div class="text-muted text-center p-2">
      {{ t('messages.conversations_not_found') }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, watch } from 'vue';
import { useI18n } from '../composables/useI18n';
import { useMessagesStore } from '../stores/messages';
import { useMessageDrafts } from '../composables/useMessageDrafts';
import { processSidebarText, processLogText } from '../utils/textHelpers';
import { logger } from '../utils/logger';

declare global {
  interface Window {
    TimezoneHelper?: {
      formatRelativeTime: (timestamp: number) => string;
      formatTime: (timestamp: number, locale: string) => string;
      getDisplayDate: (date: Date) => string;
      getCurrentLocale: () => string;
      groupMessagesByLocalDate: (messages: any[], locale: string) => any;
    };
  }
}

const { t } = useI18n();
const { loadDraft } = useMessageDrafts();
const globalStore = (window as any).__vueMessagesStore;

const getDraftText = (contactId: string | number) => loadDraft(contactId);
const localStore = useMessagesStore();
const store = globalStore || localStore;

logger.log('VueSidebar: Store initialization', {
  hasGlobalStore: !!globalStore,
  usingGlobalStore: !!globalStore,
  storeId: globalStore ? 'global' : 'local',
  globalStoreId: globalStore?.$id?.toString?.(),
  localStoreId: localStore.$id?.toString?.(),
  sameInstance: globalStore === localStore
});

const conversationsQuery = ref('');

const conversations = computed(() => {
  const convs = store.conversationOrder || [];
  logger.log('VueSidebar: conversations computed - count:', convs.length, 'with statuses:', 
    convs.map((c: any) => ({
      id: c.contact?.id,
      status: c.last_message?.status,
      is_read: c.last_message?.is_read,
      text: c.last_message?.text || '', // Без обработки для логирования
      hasMedia: c.last_message?.hasMedia
    }))
  );
  return convs;
});
const currentContactId = computed(() => store.currentContactId);
const typing = computed(() => store.typing);
const unreadCount = computed(() => store.unread);
const presence = computed(() => store.presence);

const hasContacts = computed(() => {
  const result = conversations.value && conversations.value.length > 0;
  logger.log('VueSidebar: hasContacts computed - conversations:', conversations.value?.length || 0, 'result:', result);
  return result;
});

const relativeTime = (ts: number) => {
  // Use TimezoneHelper for relative time formatting
  if (window.TimezoneHelper && typeof window.TimezoneHelper.formatRelativeTime === 'function') {
    return window.TimezoneHelper.formatRelativeTime(ts);
  }
  
  // Fallback to simple formatting if TimezoneHelper unavailable
  try {
    const now = Date.now() / 1000;
    const diff = now - ts;
    const absDiff = Math.abs(diff);

    if (absDiff < 60) return 'сейчас';
    if (absDiff < 3600) return Math.floor(absDiff / 60) + ' м';
    if (absDiff < 86400) return Math.floor(absDiff / 3600) + ' ч';
    if (absDiff < 604800) {
      const days = Math.floor(absDiff / 86400);
      return days === 1 ? 'вчера' : days + ' д';
    }

    const date = new Date(ts * 1000);
    const today = new Date();
    const thisYear = today.getFullYear();

    if (date.getFullYear() === thisYear) {
      const months = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];
      return date.getDate() + ' ' + months[date.getMonth()];
    } else {
      return date.getDate() + '.' + (date.getMonth() + 1) + '.' + date.getFullYear();
    }
  } catch (e) {
    console.error('Error formatting relative time:', ts, e);
    return '';
  }
};

const getUnreadCount = (contactId: string | number) => {
  return unreadCount.value?.[contactId] || 0;
};

const isTyping = (contactId: string | number) => {
  const t = typing.value?.[contactId];
  if (!t) return false;
  return Object.values(t).some((v: any) => v?.isTyping);
};


const getOnlineStatus = (contactId: string | number | undefined, fallback?: boolean | string): string => {
  if (contactId === undefined || contactId === null) return fallback === true ? 'online' : (typeof fallback === 'string' ? fallback : 'offline');
  if (presence.value && contactId in presence.value) return presence.value[contactId] as string;
  if (fallback === true) return 'online';
  if (typeof fallback === 'string') return fallback;
  return 'offline';
};

const hasOnlineIndicator = (contactId: string | number | undefined, fallback?: boolean | string): boolean => {
  const status = getOnlineStatus(contactId, fallback);
  return status === 'online' || status === 'recently_online';
};

const onlineClass = (contactId: string | number | undefined, fallback?: boolean | string): string => {
  const status = getOnlineStatus(contactId, fallback);
  if (status === 'online') return 'bg-green';
  if (status === 'recently_online') return 'bg-orange';
  return 'bg-gray';
};

const processSidebarMessageText = processSidebarText;

const selectContact = (id: string | number) => {
  logger.log('VueSidebar: Selecting contact', id);
  store.setCurrentContactId(id);
  (window as any).__vueSidebarStore = store;
  if (window.location.pathname === '/messages') {
    logger.log('VueSidebar: On messages page, dispatching event');
    window.dispatchEvent(new CustomEvent('vue-sidebar-contact-selected', { 
      detail: { contactId: id } 
    }));
  } else {
    logger.log('VueSidebar: Redirecting to messages page');
    window.location.href = '/messages?contact=' + id;
  }
};

const handleSearch = () => {
  // Используем поиск из store
  store.loadConversations(conversationsQuery.value);
};

// Watch for changes in last_message to debug hasMedia issues
watch(
  () => store.conversationOrder,
  (newOrder, oldOrder) => {
    if (!newOrder || !oldOrder) return;
    
    newOrder.forEach((conv: any, index: number) => {
      const oldConv = oldOrder[index];
      const lastMsg = conv.last_message;
      const oldLastMsg = oldConv?.last_message;
      
      if (lastMsg && oldLastMsg) {
        const hasMediaChanged = lastMsg.hasMedia !== oldLastMsg.hasMedia;
        const textChanged = lastMsg.text !== oldLastMsg.text;
        
        if (hasMediaChanged || textChanged) {
          logger.log('VueSidebar: last_message changed for contact', conv.contact?.id, {
            hasMedia: { old: oldLastMsg.hasMedia, new: lastMsg.hasMedia, changed: hasMediaChanged },
            text: { old: processLogText(oldLastMsg.text || ''), new: processLogText(lastMsg.text || ''), changed: textChanged },
            timestamp: lastMsg.timestamp
          });
        }
      }
    });
  },
  { deep: true }
);

// При монтировании компонента загружаем данные
onMounted(() => {
  logger.log('VueSidebar: Component mounted, starting initialization...');
  logger.log('VueSidebar: Window location:', window.location.pathname);
  logger.log('VueSidebar: Has main Vue app:', !!(window as any).__vueMessagesStore);
  logger.log('VueSidebar: Initial conversations count:', conversations.value?.length || 0);
  logger.log('VueSidebar: Initial hasContacts:', hasContacts.value);
  
  // Если есть основное Vue приложение, синхронизируем данные
  if ((window as any).__vueMessagesStore) {
    const mainStore = (window as any).__vueMessagesStore;
    logger.log('VueSidebar: Syncing with main app, main store conversations:', mainStore.conversations?.length || 0);
    
    store.conversations = mainStore.conversations;
    store.conversationOrder = mainStore.conversationOrder;
    store.currentContactId = mainStore.currentContactId;
    store.unread = mainStore.unread;
    store.typing = mainStore.typing;
    store.presence = mainStore.presence;
    logger.log('Vue Sidebar: Synchronized with main Vue app');
    logger.log('VueSidebar: After sync conversations count:', conversations.value?.length || 0);
    logger.log('VueSidebar: After sync hasContacts:', hasContacts.value);
  } else {
    // Если основного приложения нет, загружаем данные самостоятельно
    logger.log('Vue Sidebar: Loading conversations independently');
    store.loadConversations().then(() => {
      logger.log('VueSidebar: Conversations loaded, count:', conversations.value?.length || 0);
      logger.log('VueSidebar: After load hasContacts:', hasContacts.value);
    }).catch((error: any) => {
      console.error('VueSidebar: Error loading conversations:', error);
    });
  }
  
  // Проверяем рендеринг компонента
  setTimeout(() => {
    logger.log('VueSidebar: Component render check - conversations count:', conversations.value?.length || 0);
    logger.log('VueSidebar: Component render check - hasContacts:', hasContacts.value);
    logger.log('VueSidebar: DOM elements count:', document.querySelectorAll('.conversation').length);
  }, 1000);
});
</script>

<style scoped>
/* Удаляем жестко закодированные цвета для совместимости с темной/светлой темой */
/* Стили наследуются из основного CSS темы */

/* Chrome-specific fixes */
.messages-conversations-filter {
  display: block !important;
  visibility: visible !important;
}

.messages-conversations {
  display: block !important;
  visibility: visible !important;
}

.wrapper-conversations {
  display: block !important;
  visibility: visible !important;
}

.conversations {
  display: block !important;
  visibility: visible !important;
}

.conversation {
  display: flex !important;
  visibility: visible !important;
  opacity: 1 !important;
}

.input-icon {
  position: relative;
  display: block !important;
  visibility: visible !important;
}

.input-icon-addon {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #6c757d;
  z-index: 10;
}

.avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  background-color: #f8f9fa;
  position: relative;
  display: block !important;
  visibility: visible !important;
}

.avatar-status {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 2px solid #fff;
}

.bg-green {
  background-color: #28a745;
}

.typing-header-indicator-text {
  display: inline-block;
}

.typing-header-dots span {
  display: inline-block;
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background-color: #6c757d;
  margin: 0 1px;
  animation: typing-dots 1.4s infinite;
}

.typing-header-dots span:nth-child(1) {
  animation-delay: 0s;
}

.typing-header-dots span:nth-child(2) {
  animation-delay: 0.2s;
}

.typing-header-dots span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes typing-dots {
  0%, 60%, 100% {
    transform: translateY(0);
    opacity: 0.3;
  }
  30% {
    transform: translateY(-10px);
    opacity: 1;
  }
}

/* Стили для времени статусов сообщений */
.message-status-time {
  font-size: 0.75rem;
  color: #6c757d;
  white-space: nowrap;
}

/* Стили для статусов сообщений - используем те же что в диалоге */
.message-status-read,
.message-status-delivered {
  display: inline-flex;
  align-items: center;
  position: relative;
  font-size: 0.7rem;
  margin-right: 3px;
}

.message-status-read i:first-child,
.message-status-delivered i:first-child {
  position: relative;
  z-index: 1;
}

.message-status-read i:last-child,
.message-status-delivered i:last-child {
  position: relative;
  z-index: 2;
}

.message-status-read {
  color: #007bff;
}

.message-status-delivered {
  color: #6c757d;
  display: inline-flex;
  align-items: center;
  position: relative;
}

.message-status-delivered i:first-child {
  position: relative;
  z-index: 1;
}

.message-status-delivered i:last-child {
  position: relative;
  margin-left: -0.3rem;
  z-index: 2;
}

.message-status-read {
  color: #007bff;
  display: inline-flex;
  align-items: center;
  position: relative;
}

.message-status-read i:first-child {
  position: relative;
  z-index: 1;
}

.message-status-read i:last-child {
  position: relative;
  margin-left: -0.3rem;
  z-index: 2;
}

/* Анимация появления галочек */
.message-status-read i,
.message-status-delivered i {
  animation: checkmark-appear 0.3s ease-in-out;
}

@keyframes checkmark-appear {
  0% {
    opacity: 0;
    transform: scale(0.5);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}

.conversation.active .message-status-time {
  /* Не изменяем цвет иконок статуса при активном контакте */
  color: inherit !important;
}

.conversation.active .time.text-primary {
  color: #007bff !important;
}

.conversation.active .time.text-gray {
  color: #6c757d !important;
}

/* Ensure text column can shrink below its content */
.conversation .flex-grow-1 {
  min-width: 0;
}

/* Single-line ellipsis for last message text */
.message {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  flex: 1 1 auto;
}

/* Debug styles */
#vue-sidebar-root {
  display: block !important;
  visibility: visible !important;
  opacity: 1 !important;
}

#vue-sidebar-root > * {
  display: block !important;
  visibility: visible !important;
}
</style>
